/* eslint-disable @typescript-eslint/no-explicit-any */
import { useGalleryCtx } from "@/contexts/gallery";
import { useLocaleCtx } from "@/contexts/locale";
import { getGalleryPageWinners } from "@/services/gallery/gallery.client";
import { filter } from "../types/common";
import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { WinnersSectionContext } from "../types/common";
import { useSearchParams } from "next/navigation";
import { YearQuery } from "../common/filter";

const WinnersContext = createContext<WinnersSectionContext | undefined>(undefined);

export const useWinners = () => {
  const context = useContext(WinnersContext);

  if (!context) {
    throw new Error("useWinners must be used within a WinnersProvider");
  }

  return context;
};

export const WinnersProvider = ({ data, children }: { data: any; children: ReactNode }) => {
  const params = useSearchParams();

  const initialYear = {
    title: params.get(YearQuery.WINNERS_YEAR) || data.year_filter_text.years[4],
    value: params.get(YearQuery.WINNERS_YEAR) || "",
  };

  const [pageNo, setPageNo] = useState(1);
  const [pageSize] = useState(6);
  const [year, setYear] = useState<filter>(initialYear);
  const [type, setType] = useState<filter>({
    title: data.dropdown_text.values[2],
    value: "",
  });
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [usedWinnersCount, setUsedWinnersCount] = useState(0);
  const [initialDataEnded, setInitialDataEnded] = useState(false);

  const { lang } = useLocaleCtx();
  const { setWinners, winners } = useGalleryCtx();

  const appendUniqueWinners = (newData: any) => {
    setWinners((prev: any) => {
      const existingIds = prev.map((Winners: any) => Winners.id);
      const filteredData = newData.filter((Winners: any) => !existingIds.includes(Winners.id));
      return [...prev, ...filteredData];
    });
  };

  const fetchMoreWinners = async () => {
    setLoadingMore(true);

    const isFiltered = year.value !== "" || type.value !== "";

    if (isFiltered) {
      // If filters are applied, fetch from the API
      const galleryData = await getGalleryPageWinners({
        lang,
        pageNo,
        pageSize,
        year: year.value,
        type: type.value,
      });

      if (galleryData) {
        if (galleryData.data.length < 6) {
          setReachedEnd(true);
        }
        appendUniqueWinners(galleryData.data);
      }
    } else {
      // Check if there are remaining Winners from the initial data
      const remainingWinners = data.winners.slice(usedWinnersCount, usedWinnersCount + 6);

      if (remainingWinners.length > 0) {
        setWinners((prev: any) => [...prev, ...remainingWinners]);
        setUsedWinnersCount((prev) => prev + remainingWinners.length);
      } else {
        setInitialDataEnded(true);
        setPageNo(1);
      }
    }

    setLoadingMore(false);
  };

  const fetchNewWinners = async () => {
    setLoading(true);
    setReachedEnd(false);
    setInitialDataEnded(false);

    if (pageNo === 1 && year.value === "" && type.value === "") {
      const initialWinners = data.winners.slice(0, 6);
      setWinners(initialWinners);
      setUsedWinnersCount(initialWinners.length);
      setLoading(false);

      return;
    }

    const galleryData = await getGalleryPageWinners({
      lang,
      pageNo: 1,
      pageSize,
      year: year.value,
      type: type.value,
    });

    if (galleryData) {
      setWinners([...galleryData.data]);
      setUsedWinnersCount(0);
    }

    setLoading(false);
  };

  useEffect(() => {
    // Once initial data is exhausted, revert to fetching normally

    if (initialDataEnded) {
      (async () => {
        const galleryData = await getGalleryPageWinners({
          lang,
          pageNo,
          pageSize,
          year: year.value,
          type: type.value,
        });

        if (galleryData) {
          if (galleryData.data.length < 6) {
            setReachedEnd(true);
          }
          appendUniqueWinners(galleryData.data);
        }
      })();

      return;
    }

    if (pageNo > 1) {
      fetchMoreWinners();
    }
  }, [pageNo]);

  useEffect(() => {
    setPageNo(1);

    fetchNewWinners();
  }, [lang, year, type]);

  const value = {
    pageNo,
    pageSize,
    year,
    type,
    loading,
    loadingMore,
    setPageNo,
    setYear,
    setType,
    setLoading,
    setLoadingMore,
    usedWinnersCount,
    setUsedWinnersCount,
    winners,
    reachedEnd,
  };

  return <WinnersContext.Provider value={value}>{children}</WinnersContext.Provider>;
};
